import type { AnyStateMachine, InspectionEvent, SnapshotFrom } from 'xstate';
import { z } from 'zod';

import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';

import type { StateContext } from './types';

const navigationInspectionEventSchema = z
	.object({
		type: z.literal('@xstate.microstep'),
		event: z.object({ type: z.enum(['back', 'submit', 'error']) }).loose(),
		_transitions: z.array(z.object({ target: z.array(z.object({ key: z.string() })).min(1) })).min(1),
	})
	.loose();

export type NavigationInspectionEvent = Extract<InspectionEvent, { type: '@xstate.microstep' }> &
	z.infer<typeof navigationInspectionEventSchema>;

export function assertNavigationEvent(event: InspectionEvent): asserts event is NavigationInspectionEvent {
	const result = navigationInspectionEventSchema.safeParse(event);
	if (!result.success || result.data.event.target === 'self') {
		throw new Error('not a navigation event');
	}
}

// oxlint-disable-next-line typescript/no-explicit-any
type AnySnapshot = SnapshotFrom<AnyStateMachine>;
type RequiredSnapshot = Required<Pick<AnySnapshot, 'status' | 'value' | 'context'>> & {
	children: Record<string, { snapshot: RequiredSnapshot; src: string }>;
};

// TODO: update this from `metadata.variant` to flow property and remove mapping
const FLOW = {
	control: 'control',
	fake_control: 'control',
	recommendations_variant_b: 'recommendations',
	preview_edit_variant_a: 'editVariantA',
	preview_edit_variant_b: 'editVariantB',
};
// oxlint-disable-next-line typescript/no-explicit-any
export function buildSnapshot(draft: StateContext['draft'] | PetitionDraft, step: string): SnapshotFrom<any> {
	const flow = FLOW[draft?.metadata?.variant ?? 'control'];

	return {
		status: 'active',
		value: flow,
		context: { id: flow, draft },
		children: {
			[`${flow}ActorId`]: {
				snapshot: {
					status: 'active',
					value: step,
					context: { id: flow, draft },
					children: {},
				},
				src: `${flow}ActorSrc`,
			},
		},
	} satisfies RequiredSnapshot;
}
